// Record in Chat: the meeting recorder (BUILD 1 WP-4; C-8, C-10, MAP-MEETINGS §4).
//
// A zustand module singleton, so a recording survives navigation inside the
// app: the recording strip, the record control and the meeting canvas all
// read the same store.
//
//   start(): capture starts inside the click (the browser's tab picker needs
//     the gesture), frames are held in memory until the meeting exists, then
//     spooled on the device (C-7 spool), cut into 10 s PCM1 chunks and PUT to
//     Chat while the meeting runs. The spool deletes a chunk only after the
//     server acknowledges it. The live preview is a separate, best-effort
//     socket to the speech gateway.
//   stop(): the capture flushes its tail, the take ends with its exact sample
//     count, Stop is posted at once (the server seals as soon as the last
//     chunk lands) and the backlog drains, offline included.
//   boot(): after a reload, spooled takes are reconciled against the server's
//     ledger, the missing chunks uploaded and the take ended; Resume starts a
//     new take, Stop finishes the meeting.
//   retire(): the signed-in person changed. Everything this session holds is
//     let go at once (microphone, uploads, preview, the strip); the take's
//     unsent audio stays on the device, so the same person's next sign-in
//     here uploads it and offers the meeting back, as after a reload.
//
// Owner: WP-4.
import { createStore, type StoreApi } from 'zustand/vanilla';
import { useStore } from 'zustand';
import {
  CaptureError,
  type CaptureHandle,
  type CaptureLifecycle,
  type CaptureSource,
  type PcmFrame,
  type SpoolMeta,
  type SpoolSession,
} from '@/speech/contracts';
import { chunkOfSeq, CAPTURE_FRAME_SAMPLES } from './meeting-capture-chunks';
import { announceMeetingUpdated } from './meeting-events';
import {
  meetingRecorderDeps,
  SOURCES_FOR,
  type MeetingRecorderDeps,
  type RecordSourceChoice,
  type WakeLockSentinelLike,
} from './meeting-recorder-deps';
import {
  createPreviewLink,
  type PreviewLine,
  type PreviewLink,
  type PreviewPartial,
  type PreviewState,
} from './meeting-recorder-preview';
import { createChunkUploader, type ChunkUploader } from './meeting-recorder-uploader';
import { createMeetingRecorderRecovery } from './meeting-recorder-recovery';
import type { MeetingParticipantInput } from './meetings-api';
import type { MeetingCapabilities } from './types';
import {
  recordingStorageWarning,
  recordingBytes,
  STORAGE_MIN_BYTES,
  type RecordingStorageWarning,
} from './meeting-recording-storage';
export { STORAGE_MIN_BYTES } from './meeting-recording-storage';

export type RecorderPhase =
  | 'idle'
  | 'starting'
  | 'recording'
  | 'stopping'
  | 'saving'
  | 'recovering'
  | 'interrupted'
  | 'error';

/** Copy keys of ui/i18n/ns/meetings-record.ts. */
export type RecorderErrorCode =
  | 'micDenied'
  | 'nativeDenied'
  | 'nativeUnavailable'
  | 'noMic'
  | 'micBusy'
  | 'insecureOrigin'
  | 'shareCancelled'
  | 'shareNoAudio'
  | 'tabUnsupported'
  | 'storageLow'
  | 'captureClosed'
  | 'captureActiveElsewhere'
  | 'recordUnavailable'
  | 'limitReached'
  | 'stopFailed'
  | 'paymentRequired';
export type RecorderNotice =
  | 'tabEnded'
  | 'micEnded'
  | 'micLost'
  | 'limitWarning'
  | 'limitReached'
  | null;

export type RecoveryOffer = {
  meetingId: string;
  title: string | null;
  sources: CaptureSource[];
  nextTake: number;
};

export type RecorderSnapshot = {
  phase: RecorderPhase;
  meetingId: string | null;
  title: string | null;
  take: number;
  sources: CaptureSource[];
  /** Where the current take starts on the meeting timeline. */
  takeOffsetMs: number;
  /** Samples captured in the current take (16 kHz). */
  takeSamples: number;
  /** Exact acknowledged samples; null when recovered ranges lack sample receipts. */
  ackedSamples: number | null;
  /** Capture metadata can include audio lost before a reload; it is not a device backlog count. */
  recovered: boolean;
  level: number;
  sourceLevels: Partial<Record<CaptureSource, number>>;
  upload: { pendingChunks: number; online: boolean; incomplete: boolean };
  localStorage: { durable: boolean; state: 'pending' | 'stored' | 'failed' };
  storageWarning: RecordingStorageWarning;
  preview: {
    state: PreviewState;
    reason: string | null;
    lines: PreviewLine[];
    partial: PreviewPartial | null;
    notShownMs: number;
  };
  maxDurationMs: number;
  notice: RecorderNotice;
  error: RecorderErrorCode | null;
  recovery: RecoveryOffer | null;
};

export type StartRecordingInput = {
  title: string;
  titleIsDefault: boolean;
  source: RecordSourceChoice;
  systemDeviceId?: string;
  microphoneDeviceId?: string;
  participants: MeetingParticipantInput[];
  voiceIdentification?: boolean;
  onRecordingStarted?: (meetingId: string) => void;
};

const PREVIEW_LINES_KEPT = 400;
export const LIMIT_WARNING_MS = 5 * 60 * 1000;

const initialSnapshot = (): RecorderSnapshot => ({
  phase: 'idle',
  meetingId: null,
  title: null,
  take: 0,
  sources: [],
  takeOffsetMs: 0,
  takeSamples: 0,
  ackedSamples: 0,
  recovered: false,
  level: 0,
  sourceLevels: {},
  upload: { pendingChunks: 0, online: true, incomplete: false },
  localStorage: { durable: false, state: 'pending' },
  storageWarning: 'unknown',
  preview: { state: 'off', reason: null, lines: [], partial: null, notShownMs: 0 },
  maxDurationMs: 4 * 60 * 60 * 1000 - 60_000,
  notice: null,
  error: null,
  recovery: null,
});

/** The meeting-timeline position of the current take's newest sample. */
export const recorderElapsedMs = (state: RecorderSnapshot) =>
  state.takeOffsetMs + Math.round(state.takeSamples / 16);
/** Audio on this device the server has not acknowledged yet, in seconds. */
export const recorderBacklogSeconds = (state: RecorderSnapshot) =>
  state.recovered || state.ackedSamples === null
    ? null
    : Math.max(0, Math.ceil((state.takeSamples - state.ackedSamples) / 16000));
export const recorderActive = (state: RecorderSnapshot) =>
  ['starting', 'recording', 'stopping'].includes(state.phase);

const CAPTURE_ERRORS: Record<string, RecorderErrorCode> = {
  permission_denied: 'micDenied',
  native_permission_denied: 'nativeDenied',
  native_unavailable: 'nativeUnavailable',
  no_microphone: 'noMic',
  microphone_busy: 'micBusy',
  insecure_context: 'insecureOrigin',
  share_cancelled: 'shareCancelled',
  share_no_audio: 'shareNoAudio',
  tab_unsupported: 'tabUnsupported',
  unsupported: 'recordUnavailable',
};
const SERVER_ERRORS: Record<string, RecorderErrorCode> = {
  meeting_capture_active: 'captureActiveElsewhere',
  meeting_capture_closed: 'captureClosed',
  meeting_capture_limit_reached: 'limitReached',
  // The company's shared usage credit is used up and it has no card (402).
  payment_required: 'paymentRequired',
};

/** A failure's copy key. Exported for tests. */
export function recorderErrorFor(error: unknown): RecorderErrorCode {
  if (error instanceof CaptureError) return CAPTURE_ERRORS[error.code] ?? 'recordUnavailable';
  const code = (error as { code?: unknown } | null)?.code;
  return (typeof code === 'string' && SERVER_ERRORS[code]) || 'recordUnavailable';
}

const limitsOf = (value: MeetingCapabilities | null): Partial<RecorderSnapshot> =>
  value ? { maxDurationMs: value.record.maxDurationMs } : {};

class RecorderPreflightError extends Error {
  constructor(readonly recorderCode: RecorderErrorCode) {
    super(recorderCode);
  }
}

/** The signed-in person changed while a start or resume was on its way. */
class RecorderRetiredError extends Error {}

export type Active = {
  deps: MeetingRecorderDeps;
  owner: string;
  /** The signed-in session it belongs to (see retire). */
  generation: number;
  meetingId: string | null;
  take: number;
  capture: CaptureHandle | null;
  held: PcmFrame[];
  session: SpoolSession | null;
  uploader: ChunkUploader | null;
  preview: PreviewLink | null;
  releaseMic: (() => void) | null;
  /** This tab's hold on the take's spool (see TakeLock). */
  releaseLock: (() => void) | null;
  lastSeq: number;
  totalSamples: number;
  ackedSamples: number | null;
  recovered: boolean;
  /** Current server-held or matching-acknowledged chunks of this same take. */
  ackedChunks: Set<number>;
  incomplete: boolean;
  view: Pick<RecorderSnapshot, 'title' | 'sources' | 'takeOffsetMs'>;
  wakeLock: WakeLockSentinelLike | null;
  unguard: (() => void) | null;
  unwatchStorage: (() => void) | null;
};

/** A settled request: its value, or the status that refused it (0: never answered). */
export type Settled<T> = { ok: true; value: T } | { ok: false; status: number };

/**
 * A refusal Retry can fix: signed out, forbidden, never answered. A 409 or
 * 404 means the meeting is already closed or gone, so there is nothing to stop.
 */
const stopRefused = (result: Settled<unknown>) =>
  !result.ok && result.status !== 409 && result.status !== 404;

export type MeetingRecorder = ReturnType<typeof createMeetingRecorder>;

export function createMeetingRecorder(getDeps: () => MeetingRecorderDeps | null) {
  const store: StoreApi<RecorderSnapshot> = createStore<RecorderSnapshot>(initialSnapshot);
  const patch = (next: Partial<RecorderSnapshot>) => store.setState(next);
  let active: Active | null = null;
  let capabilities: MeetingCapabilities | null = null;
  const booted = new Set<string>();
  /** What Retry runs after `stopFailed`. */
  let retryClose: (() => Promise<void>) | null = null;
  /** Bumped when the signed-in person changes: work of an earlier session stops. */
  let generation = 0;
  /** Ended takes stay owned until complete, discarded or released on retire. */
  const draining = new Set<Active>();

  const ownedRuns = () => [...new Set([...draining, ...(active ? [active] : [])])];
  function updateUploads() {
    const runs = ownedRuns();
    patch({
      upload: {
        pendingChunks: runs.reduce((sum, run) => sum + (run.uploader?.pending() ?? 0), 0),
        online: runs.every((run) => run.uploader?.online() !== false),
        incomplete: runs.some((run) => run.incomplete),
      },
    });
  }

  function watchStorage(run: Active) {
    run.unwatchStorage?.();
    const update = () => {
      if (run !== active || !run.session) return;
      patch({
        localStorage: { durable: run.deps.spool.durable, state: run.session.storageState() },
      });
    };
    run.unwatchStorage = run.session?.onStorageChange(update) ?? null;
    update();
  }

  async function checkStorage(run: Active) {
    const estimate = await run.deps.storage?.estimate?.().catch(() => null);
    if (run !== active) return;
    const state = store.getState();
    const pendingBytes =
      state.localStorage.durable && state.localStorage.state === 'stored'
        ? 0
        : recordingBytes(Math.max(0, state.takeSamples - (state.ackedSamples ?? 0)) / 16);
    patch({
      storageWarning: recordingStorageWarning(
        estimate,
        Math.max(0, state.maxDurationMs - recorderElapsedMs(state)),
        pendingBytes,
      ),
    });
  }

  // ── Frames ──────────────────────────────────────────────────────────────
  function spoolFrame(run: Active, frame: PcmFrame) {
    run.session!.append(frame);
    if (frame.seq % 100 === 99 && frame.samples === CAPTURE_FRAME_SAMPLES)
      run.uploader?.enqueue(chunkOfSeq(frame.seq));
  }

  function checkLimit(run: Active, elapsed: number) {
    const { maxDurationMs, notice } = store.getState();
    if (elapsed >= maxDurationMs) {
      void stop('limitReached');
      return;
    }
    if (elapsed >= maxDurationMs - LIMIT_WARNING_MS && notice !== 'limitWarning' && run === active)
      patch({ notice: 'limitWarning' });
  }

  function onFrame(run: Active, frame: PcmFrame) {
    // A retired run keeps nothing: its capture may still flush while stopping.
    if (run !== active) return;
    run.lastSeq = frame.seq;
    run.totalSamples = frame.sampleOffset + frame.samples;
    if (run.session) spoolFrame(run, frame);
    else run.held.push(frame);
    run.preview?.sendFrame(frame);
    patch({ takeSamples: run.totalSamples });
    if (frame.seq % 300 === 299) void checkStorage(run);
    checkLimit(run, store.getState().takeOffsetMs + Math.round(run.totalSamples / 16));
  }

  const LIFECYCLE: Partial<Record<CaptureLifecycle, (run: Active) => void>> = {
    // Both: the take continues from the microphone (S3). A tab-only meeting ends with its tab.
    'tab-ended': (run) => {
      if (store.getState().sources.includes('mic')) patch({ notice: 'tabEnded' });
      else if (run === active) void stop();
    },
    'mic-ended': (run) => {
      if (store.getState().sources.includes('tab')) patch({ notice: 'micEnded' });
      else if (run === active) void interrupt();
    },
    // Pause rather than silently lose the remote side of the conversation.
    'system-ended': (run) => {
      if (run === active) void interrupt();
    },
  };

  // ── Preview ─────────────────────────────────────────────────────────────
  function previewEvents() {
    return {
      onState: (state: PreviewState, reason: string | null) =>
        patch({ preview: { ...store.getState().preview, state, reason } }),
      onPartial: (partial: PreviewPartial | null) =>
        patch({ preview: { ...store.getState().preview, partial } }),
      onFinal: (line: PreviewLine) => {
        const preview = store.getState().preview;
        const lines = [...preview.lines.filter((item) => item.key !== line.key), line].slice(
          -PREVIEW_LINES_KEPT,
        );
        patch({ preview: { ...preview, lines } });
      },
      onNotShown: (ms: number) => {
        const preview = store.getState().preview;
        patch({ preview: { ...preview, notShownMs: preview.notShownMs + ms } });
      },
    };
  }

  function openPreview(run: Active, offsetMs: number) {
    if (!capabilities?.preview.available || !run.meetingId) {
      patch({
        preview: { ...store.getState().preview, state: 'unavailable', reason: 'not_configured' },
      });
      return;
    }
    run.preview = createPreviewLink({
      deps: run.deps,
      meetingId: run.meetingId,
      offsetMs,
      events: previewEvents(),
    });
    void run.preview.start();
  }

  // ── Uploads ─────────────────────────────────────────────────────────────
  function uploaderFor(run: Active, meetingId: string, take: number, session: SpoolSession) {
    const current = () => run === active && store.getState().take === take;
    return createChunkUploader({
      meetingId,
      take,
      session,
      deps: {
        putChunk: (id, takeNumber, chunk, bytes, sha256) =>
          run.deps.api.putChunk(id, takeNumber, chunk, bytes, sha256),
        sha256: (bytes) => run.deps.sha256(bytes),
        sleep: (ms) => run.deps.sleep(ms),
        random: () => run.deps.random(),
      },
      events: {
        onAck: ({ chunk, samples, stopRequested }) => {
          if (!run.ackedChunks.has(chunk)) {
            run.ackedChunks.add(chunk);
            if (run.ackedSamples !== null) run.ackedSamples += samples;
          }
          if (current()) patch({ ackedSamples: run.ackedSamples });
          // Stopped from another device or tab: drain and end this take.
          if (stopRequested && run === active && store.getState().phase === 'recording')
            void stop();
        },
        onPending: updateUploads,
        onOnline: updateUploads,
        onFatal: (failure) => {
          if (run === active) void fail(failure);
        },
      },
    });
  }

  /** Queue every chunk of an ended take that is not queued yet; the last is FINAL. */
  function queueRest(uploader: ChunkUploader, lastSeq: number, held: Set<number> = new Set()) {
    if (lastSeq < 0) return;
    const last = chunkOfSeq(lastSeq);
    for (let chunk = 0; chunk <= last; chunk += 1)
      if (!uploader.has(chunk) && !held.has(chunk)) uploader.enqueue(chunk, chunk === last);
  }

  // ── Lifecycle guards ────────────────────────────────────────────────────
  function guardPage(run: Active) {
    run.unguard?.();
    const events = run.deps.pageEvents;
    if (!events) return;
    const beforeUnload = (event: Event) => {
      if (
        !recorderActive(store.getState()) &&
        store.getState().phase !== 'saving' &&
        draining.size === 0
      )
        return;
      event.preventDefault();
      (event as BeforeUnloadEvent).returnValue = '';
    };
    // The browser releases the wake lock whenever the page is hidden; ask
    // again when it is shown (a request while hidden is refused, harmlessly).
    const visible = () => {
      if (run === active && run.capture && (!run.wakeLock || run.wakeLock.released))
        void lockScreen(run);
    };
    events.addEventListener('beforeunload', beforeUnload);
    events.addEventListener('visibilitychange', visible);
    run.unguard = () => {
      events.removeEventListener('beforeunload', beforeUnload);
      events.removeEventListener('visibilitychange', visible);
    };
  }

  async function lockScreen(run: Active) {
    try {
      run.wakeLock = (await run.deps.wakeLock?.request('screen')) ?? null;
    } catch {
      run.wakeLock = null;
    }
  }

  async function releaseDevice(run: Active) {
    const capture = run.capture;
    run.capture = null;
    const result = capture ? await capture.stop().catch(() => null) : null;
    // The flush may deliver the tail; never report fewer frames than were spooled.
    if (result) {
      run.lastSeq = Math.max(run.lastSeq, result.lastSeq);
      run.totalSamples = Math.max(run.totalSamples, result.totalSamples);
    }
    run.releaseMic?.();
    run.releaseMic = null;
    void run.wakeLock?.release().catch(() => {});
    run.wakeLock = null;
    await run.preview?.close();
    run.preview = null;
  }

  // ── Start ───────────────────────────────────────────────────────────────
  /** What the browser can do, checked before anything prompts. */
  function supportChecks(deps: MeetingRecorderDeps, sources: CaptureSource[]) {
    const support = deps.captureSupport();
    if (!support.secure) throw new RecorderPreflightError('insecureOrigin');
    if ((sources.includes('mic') || sources.includes('system')) && !support.mic)
      throw new RecorderPreflightError('noMic');
    if (sources.includes('tab') && (!support.tab || deps.isMobile()))
      throw new RecorderPreflightError('tabUnsupported');
  }

  async function preflight(run: Active) {
    const deps = run.deps;
    capabilities ??= await deps.api.capabilities();
    if (!capabilities.record.available) throw new RecorderPreflightError('recordUnavailable');
    const estimate = await deps.storage?.estimate?.().catch(() => null);
    if (estimate?.quota && estimate.quota - (estimate.usage ?? 0) < STORAGE_MIN_BYTES)
      throw new RecorderPreflightError('storageLow');
    if (run === active)
      patch({
        storageWarning: recordingStorageWarning(estimate, capabilities.record.maxDurationMs),
      });
    void deps.storage?.persist?.().catch(() => false);
  }

  function newRun(deps: MeetingRecorderDeps, owner: string): Active {
    return {
      deps,
      owner,
      generation,
      meetingId: null,
      take: 0,
      capture: null,
      held: [],
      session: null,
      uploader: null,
      preview: null,
      releaseMic: null,
      releaseLock: null,
      lastSeq: -1,
      totalSamples: 0,
      ackedSamples: 0,
      recovered: false,
      ackedChunks: new Set(),
      incomplete: false,
      view: { title: null, sources: [], takeOffsetMs: 0 },
      wakeLock: null,
      unguard: null,
      unwatchStorage: null,
    };
  }

  async function beginCapture(
    run: Active,
    sources: CaptureSource[],
    systemDeviceId?: string,
    deviceId?: string,
  ) {
    patch({ sourceLevels: {} });
    run.releaseMic = run.deps.micOwner.claim('meeting', () => {});
    if (!run.releaseMic) throw new RecorderPreflightError('captureActiveElsewhere');
    run.capture = await run.deps.startCapture({
      sources,
      purpose: 'meeting',
      systemDeviceId,
      deviceId,
      onFrame: (frame) => onFrame(run, frame),
      onLevel: (level) => {
        if (run === active) patch({ level });
      },
      onSourceLevels: (sourceLevels) => {
        if (run === active) patch({ sourceLevels });
      },
      onLifecycle: (event) => LIFECYCLE[event]?.(run),
    });
  }

  const spoolId = (meetingId: string, take: number) => `meeting:${meetingId}:${take}`;

  /** Give up this tab's hold on the run's take, so a reload or another tab may recover it. */
  function unlock(run: Active) {
    run.unwatchStorage?.();
    run.unwatchStorage = null;
    run.releaseLock?.();
    run.releaseLock = null;
  }

  /** Open take `take` of the run's meeting: spool, server take, uploader, preview. */
  async function openTake(run: Active, take: number, sources: CaptureSource[]) {
    const meetingId = run.meetingId!;
    const startedAt = new Date(run.capture?.startedAt ?? run.deps.now()).toISOString();
    run.take = take;
    run.releaseLock = await run.deps.takeLock.tryHold(spoolId(meetingId, take));
    const session = await run.deps.spool.open({
      id: spoolId(meetingId, take),
      owner: run.owner,
      kind: 'meeting',
      meetingId,
      take,
      createdAt: run.deps.now(),
      state: 'capturing',
      lastSeq: -1,
      samples: 0,
    });
    const opened = await run.deps.api.putTake(meetingId, take, {
      sources,
      sampleRate: 16000,
      startedAt,
      deviceId: run.deps.deviceId(),
    });
    run.uploader = uploaderFor(run, meetingId, take, session);
    run.session = session;
    watchStorage(run);
    patch({ take, takeOffsetMs: opened.offsetMs, ackedSamples: 0, recovered: false });
    const state = store.getState();
    run.view = { title: state.title, sources: state.sources, takeOffsetMs: opened.offsetMs };
    for (const frame of run.held.splice(0)) spoolFrame(run, frame);
    openPreview(run, opened.offsetMs);
  }

  async function start(owner: string, input: StartRecordingInput) {
    const deps = getDeps();
    const phase = store.getState().phase;
    if (!deps || !['idle', 'error'].includes(phase)) {
      if (!deps) patch({ phase: 'error', error: 'recordUnavailable' });
      return;
    }
    const sources = SOURCES_FOR[input.source];
    const run = newRun(deps, owner);
    active = run;
    patch({
      ...initialSnapshot(),
      phase: 'starting',
      sources,
      title: input.title,
    });
    const current = () => {
      if (active !== run) throw new RecorderRetiredError();
    };
    try {
      supportChecks(deps, sources);
      // The capture opens first, inside the click: the tab picker needs the gesture.
      const checks = preflight(run);
      checks.catch(() => {});
      await beginCapture(run, sources, input.systemDeviceId, input.microphoneDeviceId);
      await checks;
      current();
      patch(limitsOf(capabilities));
      const started = await deps.api.startRecording({
        clientCaptureId: deps.uuid(),
        title: input.title,
        titleIsDefault: input.titleIsDefault,
        voiceIdentification: input.voiceIdentification ?? true,
        // Nobody picks a spoken language: the server's hints apply.
        languages: [],
        sources,
        participants: input.participants,
        consentAttested: true,
      });
      current();
      run.meetingId = started.meeting.id;
      patch({ meetingId: started.meeting.id });
      await openTake(run, 0, sources);
      current();
      guardPage(run);
      void lockScreen(run);
      patch({ phase: 'recording' });
      input.onRecordingStarted?.(started.meeting.id);
      announceMeetingUpdated();
    } catch (error) {
      // Retired meanwhile: whatever opened after the retire is let go too.
      if (active !== run) await letGo(run);
      else await abandonStart(run, error);
    }
  }

  async function abandonStart(run: Active, error: unknown) {
    await releaseDevice(run);
    run.uploader?.stop();
    run.unguard?.();
    if (run.meetingId) await run.deps.api.discardCapture(run.meetingId).catch(() => null);
    if (run.session) await run.deps.spool.remove(run.session.meta().id).catch(() => {});
    unlock(run);
    if (active === run) active = null;
    const code =
      error instanceof RecorderPreflightError ? error.recorderCode : recorderErrorFor(error);
    patch({ ...initialSnapshot(), phase: 'error', error: code });
  }

  // ── Stop ────────────────────────────────────────────────────────────────
  /** Retry a small idempotent request until it lands or the server refuses it. */
  async function settle<T>(run: Active, request: () => Promise<T>): Promise<Settled<T>> {
    for (let tries = 0; tries < 50 && run.generation === generation; tries += 1) {
      try {
        return { ok: true, value: await request() };
      } catch (error) {
        const status = (error as { status?: number }).status ?? 0;
        if (status >= 400 && status < 500 && status !== 429) return { ok: false, status };
        await run.deps.sleep(Math.min(30_000, 1000 * 2 ** Math.min(tries, 5)));
      }
    }
    return { ok: false, status: 0 };
  }

  async function endTake(
    run: Active,
    reason: 'stop' | 'reload' | 'crash',
    held: Set<number> = new Set(),
  ): Promise<Settled<unknown>> {
    const meetingId = run.meetingId!;
    await run.session?.setState('stopped', {
      lastSeq: run.lastSeq,
      samples: run.totalSamples,
    });
    if (run.uploader) queueRest(run.uploader, run.lastSeq, held);
    return settle(run, () =>
      run.deps.api.endTake(meetingId, run.take, {
        lastSeq: run.lastSeq,
        totalSamples: run.totalSamples,
        endedAt: new Date(run.deps.now()).toISOString(),
        reason,
      }),
    );
  }

  /** Remove a take's spooled audio once the server holds all of it; then let go of the take. */
  async function forgetWhenDrained(run: Active) {
    draining.add(run);
    await run.uploader?.drain();
    const complete = run.uploader?.complete() ?? false;
    run.incomplete = !complete;
    if (complete && run.session) await run.deps.spool.remove(run.session.meta().id).catch(() => {});
    if (complete) {
      run.unguard?.();
      run.unguard = null;
      unlock(run);
      draining.delete(run);
    }
    if (run.generation === generation) updateUploads();
    return complete;
  }

  function showRetainedTake() {
    const run = [...draining].find((item) => item.generation === generation);
    if (!run) return false;
    active = run;
    patch({
      ...initialSnapshot(),
      ...limitsOf(capabilities),
      ...run.view,
      phase: 'saving',
      meetingId: run.meetingId,
      take: run.take,
      takeSamples: run.totalSamples,
      ackedSamples: run.ackedSamples,
      recovered: run.recovered,
    });
    watchStorage(run);
    guardPage(run);
    updateUploads();
    return true;
  }

  async function finish(run: Active, reason: RecorderNotice = null) {
    const complete = await forgetWhenDrained(run);
    // A fatal upload failure has already moved the recorder to its error.
    if (active !== run) return;
    if (!complete) {
      patch({ phase: 'saving', upload: { ...store.getState().upload, incomplete: true } });
      return;
    }
    run.unguard?.();
    active = null;
    if (showRetainedTake()) return;
    // Stopped by the length limit: say so once the audio is safe.
    patch(
      reason === 'limitReached'
        ? { ...initialSnapshot(), phase: 'error', error: 'limitReached' }
        : { ...initialSnapshot() },
    );
    announceMeetingUpdated();
  }

  async function stop(notice: RecorderNotice = null) {
    const run = active;
    if (!run?.meetingId || store.getState().phase !== 'recording') return;
    patch({ phase: 'stopping', notice });
    await releaseDevice(run);
    // From here on the only work left is getting this device's audio to Chat,
    // which the saving state shows (offline included).
    patch({ phase: 'saving' });
    await closeTake(run, notice);
  }

  /**
   * End the take and post Stop at once (the server seals as soon as the last
   * chunk lands). A refusal Retry can fix, such as a signed-out session, is
   * shown with Retry instead of leaving the meeting recording on the server.
   */
  async function closeTake(run: Active, notice: RecorderNotice) {
    const meetingId = run.meetingId!;
    const ended = await endTake(run, 'stop');
    const stopped = stopRefused(ended)
      ? ended
      : await settle(run, () => run.deps.api.stopCapture(meetingId));
    if (await failedToStop(run, stopped, () => closeTake(run, notice))) return;
    await finish(run, notice);
  }

  /** True when the stop was refused for a reason Retry can fix (and shows it). */
  async function failedToStop(run: Active, result: Settled<unknown>, retry: () => Promise<void>) {
    if (active !== run) return true;
    if (!stopRefused(result)) {
      announceMeetingUpdated();
      return false;
    }
    retryClose = retry;
    patch({ error: 'stopFailed' });
    return true;
  }

  /** The device went away mid-meeting: end the take, keep the meeting open for Resume. */
  async function interrupt() {
    const run = active;
    if (!run?.meetingId) return;
    await releaseDevice(run);
    await endTake(run, 'crash');
    drainInBackground(run);
    const state = store.getState();
    patch({
      phase: 'interrupted',
      notice: 'micLost',
      recovery: {
        meetingId: run.meetingId,
        title: state.title,
        sources: state.sources,
        nextTake: run.take + 1,
      },
    });
  }

  async function fail(failure: 'captureClosed' | 'limitReached') {
    const run = active;
    if (!run) return;
    await releaseDevice(run);
    run.uploader?.stop();
    run.unguard?.();
    unlock(run);
    active = null;
    patch({ ...initialSnapshot(), phase: 'error', error: failure });
    announceMeetingUpdated();
  }

  async function discard() {
    const run = active;
    const meetingId = run?.meetingId ?? store.getState().recovery?.meetingId;
    if (!meetingId) return;
    const runs = ownedRuns().filter((item) => item.meetingId === meetingId);
    await Promise.all(runs.map(letGo));
    const deps = run?.deps ?? getDeps();
    await deps?.api.discardCapture(meetingId).catch(() => null);
    for (const meta of (await deps?.spool.list(run?.owner ?? '', 'meeting').catch(() => [])) ?? [])
      if (meta.meetingId === meetingId) await deps?.spool.remove(meta.id).catch(() => {});
    for (const item of runs) draining.delete(item);
    active = null;
    if (!showRetainedTake()) patch({ ...initialSnapshot() });
    announceMeetingUpdated();
  }

  const recovery = createMeetingRecorderRecovery({
    generation: () => generation,
    newRun,
    uploaderFor,
    queueRest,
    endTake,
  });

  function drainInBackground(run: Active) {
    void forgetWhenDrained(run).then(() => {
      if (active === run && store.getState().phase === 'saving') void finish(run);
    });
  }

  async function boot(owner: string) {
    const deps = getDeps();
    if (!deps || booted.has(owner) || active) return;
    booted.add(owner);
    const mine = generation;
    let sessions: SpoolMeta[];
    try {
      sessions = await deps.spool.list(owner, 'meeting');
    } catch {
      booted.delete(owner);
      return;
    }
    for (const meta of sessions) {
      let recovered: Awaited<ReturnType<typeof recovery.recoverSession>>;
      try {
        recovered = await recovery.recoverSession(deps, owner, meta.id, mine);
      } catch {
        // A network, 401 or 5xx response must leave the take on disk and make
        // this owner eligible for another boot attempt.
        booted.delete(owner);
        break;
      }
      if (!recovered) continue;
      // Signed out meanwhile: this take waits for the next sign-in.
      if (mine !== generation) {
        await letGo(recovered.run);
        continue;
      }
      // Every recovered take uploads what is left and then frees its spool;
      // the first one found is the one offered back.
      if (active) {
        drainInBackground(recovered.run);
        continue;
      }
      const { run, state } = recovered;
      active = run;
      const meeting = await deps.api.get?.(run.meetingId!).catch(() => null);
      if (active !== run) continue;
      const takes = state.takes.map((take) => take.take);
      run.view = {
        title: meeting?.title ?? null,
        sources: meeting?.captureSources ?? [],
        takeOffsetMs: 0,
      };
      patch({
        ...initialSnapshot(),
        phase: 'recovering',
        meetingId: run.meetingId,
        take: run.take,
        takeSamples: run.totalSamples,
        ackedSamples: run.ackedSamples,
        recovered: run.recovered,
        title: meeting?.title ?? null,
        sources: meeting?.captureSources ?? [],
        recovery: {
          meetingId: run.meetingId!,
          title: meeting?.title ?? null,
          sources: meeting?.captureSources?.length ? meeting.captureSources : ['mic'],
          nextTake: Math.max(run.take, ...takes) + 1,
        },
      });
      watchStorage(run);
      guardPage(run);
      drainInBackground(run);
    }
  }

  /** From recovery or an interruption: a new take of the same meeting. */
  async function resume(systemDeviceId?: string, microphoneDeviceId?: string) {
    const offer = store.getState().recovery;
    const deps = active?.deps ?? getDeps();
    if (!offer || !deps) return;
    const previous = active;
    const run = newRun(deps, previous?.owner ?? '');
    run.meetingId = offer.meetingId;
    active = run;
    patch({
      phase: 'starting',
      recovery: null,
      notice: null,
      error: null,
      sources: offer.sources,
      takeSamples: 0,
    });
    try {
      supportChecks(deps, offer.sources);
      const checks = preflight(run);
      checks.catch(() => {});
      await beginCapture(run, offer.sources, systemDeviceId, microphoneDeviceId);
      await checks;
      if (active !== run) throw new RecorderRetiredError();
      patch(limitsOf(capabilities));
      await openTake(run, offer.nextTake, offer.sources);
      if (active !== run) throw new RecorderRetiredError();
      guardPage(run);
      void lockScreen(run);
      patch({ phase: 'recording' });
    } catch (error) {
      if (active !== run) return letGo(run);
      await releaseDevice(run);
      unlock(run);
      active = previous;
      patch({ phase: 'interrupted', recovery: offer, error: recorderErrorFor(error) });
    }
  }

  /** From recovery or an interruption: finish the meeting without another take. */
  async function stopRecovered() {
    const run = active;
    const offer = store.getState().recovery;
    if (!run || !offer) return;
    patch({ phase: 'saving', recovery: null });
    await postStop(run, offer.meetingId);
  }

  async function postStop(run: Active, meetingId: string) {
    const stopped = await settle(run, () => run.deps.api.stopCapture(meetingId));
    if (await failedToStop(run, stopped, () => postStop(run, meetingId))) return;
    await finish(run);
  }

  /** Retry after `stopFailed`. */
  async function retryStop() {
    const again = retryClose;
    retryClose = null;
    if (!again || store.getState().error !== 'stopFailed') return;
    patch({ error: null });
    await again();
  }

  /** Only retry unacknowledged chunks of the same ended take, with identical PCM bytes. */
  async function retryUploads() {
    for (const run of ownedRuns()) {
      if (!run.incomplete || !run.meetingId || !run.session || run.capture) continue;
      run.incomplete = false;
      run.uploader = uploaderFor(run, run.meetingId, run.take, run.session);
      queueRest(run.uploader, run.lastSeq, run.ackedChunks);
      await finish(run);
    }
  }

  /** Dismissing `stopFailed` lets go of the meeting here; Stop and save stays on its page. */
  function dismissError() {
    const { error, phase } = store.getState();
    const run = active;
    if (error === 'stopFailed' && run) {
      retryClose = null;
      patch({ error: null });
      void finish(run);
      return;
    }
    patch({ error: null, phase: phase === 'error' ? 'idle' : phase });
  }

  /** Let go of a run of an earlier session: device, uploads, page guards and take lock. */
  async function letGo(run: Active) {
    run.uploader?.stop();
    run.unguard?.();
    await releaseDevice(run);
    await run.session?.flush().catch(() => {});
    unlock(run);
  }

  /** The signed-in person changed: see the header. Nothing is uploaded or purged here. */
  async function retire() {
    const runs = ownedRuns();
    generation += 1;
    active = null;
    draining.clear();
    retryClose = null;
    booted.clear();
    capabilities = null;
    patch({ ...initialSnapshot() });
    await Promise.all(runs.map(letGo));
  }

  return {
    store,
    start,
    stop: () => stop(),
    discard,
    boot,
    resume,
    stopRecovered,
    retryStop,
    retryUploads,
    retire,
    /** Cached so the click itself does not wait on the network. */
    async prepare(): Promise<MeetingCapabilities | null> {
      const deps = getDeps();
      if (!deps) return null;
      capabilities = await deps.api.capabilities().catch(() => capabilities);
      return capabilities;
    },
    dismissError,
    dismissNotice: () => patch({ notice: null }),
  };
}

/** The app-wide recorder. */
export const meetingRecorder = createMeetingRecorder(meetingRecorderDeps);

export function useMeetingRecorder<T>(selector: (state: RecorderSnapshot) => T): T {
  return useStore(meetingRecorder.store, selector);
}
